import { useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { STAFF_EMPTY, GENRES, COUNTRIES } from "@/lib/staffFields";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  CheckCircle2, IdCard, MapPin, Baby, CreditCard, Upload, Loader2, FileCheck2,
} from "lucide-react";

const initial = { ...STAFF_EMPTY, pays: "FRANCE", nationalite: "FRANCE", pays_naissance: "FRANCE" };

function FileField({ label, hint, pathKey, nameKey, form, setField, required }) {
  const [busy, setBusy] = useState(false);
  const done = Boolean(form[pathKey]);

  const onChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Fichier trop volumineux (max 10 Mo)"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/ouvreuses/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setField(pathKey, data.path);
      setField(nameKey, data.filename);
      toast.success("Fichier envoyé");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <Label className="text-sm font-medium">{label}{required ? " *" : ""}</Label>
      <label className={`mt-1 flex items-center justify-center gap-2 p-3 rounded-lg border-2 border-dashed cursor-pointer transition-colors text-sm ${done ? "border-primary/60 bg-primary/10 text-primary" : "border-border bg-secondary/40 text-muted-foreground hover:bg-secondary/60"}`}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : done ? <FileCheck2 className="h-4 w-4" /> : <Upload className="h-4 w-4" />}
        <span className="truncate max-w-[220px]">{busy ? "Envoi..." : done ? (form[nameKey] || "Fichier envoyé") : "Choisir un fichier (PDF/image, max 10 Mo)"}</span>
        <input className="hidden" type="file" accept="application/pdf,image/*" onChange={onChange} data-testid={`ouv-upload-${pathKey}`} />
      </label>
      {hint && <div className="text-[11px] text-muted-foreground mt-1">{hint}</div>}
    </div>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <div className="rounded-xl border border-border bg-card shadow-sm p-5 sm:p-6 space-y-4">
      <div className="text-[10px] uppercase tracking-widest text-primary font-bold flex items-center gap-1.5">
        <Icon className="h-3 w-3" /> {title}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>
    </div>
  );
}

export default function OuvreuseRegistrationPage() {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const F = (k, label, { required, placeholder, hint, type, list, colSpan } = {}) => (
    <div className={colSpan ? "md:col-span-2" : ""}>
      <Label className="text-sm font-medium">{label}{required ? " *" : ""}</Label>
      <Input value={form[k] ?? ""} onChange={(e) => set(k, e.target.value)} placeholder={placeholder}
        type={type} list={list} data-testid={`ouv-field-${k}`} className="mt-1" />
      {hint && <div className="text-[11px] text-muted-foreground mt-1">{hint}</div>}
    </div>
  );

  const submit = async () => {
    const req = {
      genre: "Civilité", nom: "Nom", prenom: "Prénom", telephone: "Téléphone", email: "Mail",
      adresse: "Adresse", code_postal: "Code postal", ville: "Ville", nationalite: "Nationalité",
      date_naissance: "Date de naissance", lieu_naissance: "Ville de naissance",
      departement_naissance: "Département de naissance", num_secu: "N° de sécurité sociale",
      iban: "IBAN", bic: "BIC",
    };
    for (const [k, label] of Object.entries(req)) {
      if (!String(form[k] || "").trim()) { toast.error(`Champ obligatoire manquant : ${label}`); return; }
    }
    if (!form.rib_path) { toast.error("Veuillez joindre votre RIB"); return; }
    const payload = {};
    Object.keys(STAFF_EMPTY).forEach((k) => (payload[k] = form[k] ?? ""));
    payload.vm_en_attente = false;
    setSaving(true);
    try {
      await api.post("/ouvreuses/register", payload);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border bg-card">
        <div className="mx-auto max-w-3xl px-6 py-4 flex items-center gap-3">
          <img src="/logo.svg" alt="Les Derniers Couchés" className="h-9 w-9" />
          <div className="leading-tight">
            <div className="font-display font-bold text-sm tracking-tight">LES DERNIERS COUCHÉS</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-primary/80">Inscription hôte/hôtesse</div>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 sm:px-6 py-10">
        {done ? (
          <div className="rounded-xl border border-border bg-card p-10 text-center" data-testid="ouv-registration-success">
            <CheckCircle2 className="h-14 w-14 text-primary mx-auto mb-4" />
            <h1 className="font-display text-2xl font-extrabold mb-2">Merci, votre fiche est enregistrée !</h1>
            <p className="text-muted-foreground">L'équipe de production a bien reçu vos informations et vous recontactera si besoin.</p>
          </div>
        ) : (
          <>
            <datalist id="countries-list-ouv">
              {COUNTRIES.map((c) => <option key={c} value={c} />)}
            </datalist>

            <div className="mb-6">
              <div className="text-xs uppercase tracking-widest text-primary font-semibold mb-1">Hôte/hôtesse · inscription</div>
              <h1 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight">Renseignez votre fiche</h1>
              <p className="text-xs text-muted-foreground mt-2">Tous les champs marqués d'un * sont obligatoires.</p>
            </div>

            <div className="space-y-4">
              <Section icon={IdCard} title="Identité">
                <div>
                  <Label className="text-sm font-medium">Civilité *</Label>
                  <Select value={form.genre || ""} onValueChange={(v) => set("genre", v)}>
                    <SelectTrigger className="mt-1" data-testid="ouv-field-genre"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>{GENRES.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div />
                {F("nom", "Nom", { required: true })}
                {F("prenom", "Prénom", { required: true })}
                {F("nom_naissance", "Nom de naissance", { placeholder: "Si différent", colSpan: true })}
                {F("telephone", "Téléphone", { required: true })}
                {F("email", "Mail", { required: true, type: "email" })}
              </Section>

              <Section icon={MapPin} title="Adresse">
                {F("adresse", "Adresse", { required: true, colSpan: true })}
                {F("code_postal", "Code postal", { required: true })}
                {F("ville", "Ville", { required: true })}
                {F("pays", "Pays", { list: "countries-list-ouv", placeholder: "FRANCE", hint: "Obligatoire si différent de France" })}
                {F("nationalite", "Nationalité", { required: true, list: "countries-list-ouv", placeholder: "FRANCE", hint: "Obligatoire si différente" })}
              </Section>

              <Section icon={Baby} title="Naissance">
                {F("date_naissance", "Né(e) le", { required: true, placeholder: "JJ/MM/AAAA" })}
                {F("lieu_naissance", "À (ville)", { required: true })}
                {F("departement_naissance", "Département", { required: true, placeholder: "99 si hors France", hint: "Mettre 99 si né(e) hors France" })}
                {F("pays_naissance", "Pays de naissance", { list: "countries-list-ouv", placeholder: "FRANCE" })}
              </Section>

              <Section icon={CreditCard} title="Sécurité sociale & IBAN">
                {F("num_secu", "NIR (n° de sécurité sociale)", { required: true, placeholder: "1 84 07 75 116 246 13", colSpan: true })}
                {F("iban", "IBAN", { required: true, placeholder: "FR76 ..." })}
                {F("bic", "BIC", { required: true })}
                <div className="md:col-span-2">
                  <FileField label="Relevé d'Identité Bancaire (RIB)" hint="PDF de préférence" required
                    pathKey="rib_path" nameKey="rib_name" form={form} setField={set} />
                </div>
              </Section>

              <div className="flex justify-end pt-2">
                <Button onClick={submit} disabled={saving}
                  className="h-12 px-8 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold" data-testid="ouv-reg-submit">
                  {saving ? "Envoi..." : "Envoyer mon inscription"}
                </Button>
              </div>
            </div>

            <div className="text-center text-xs text-muted-foreground mt-8">
              Les Derniers Couchés · Gestion des concerts
            </div>
          </>
        )}
      </main>
    </div>
  );
}
